import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronsUpDown, Check, type LucideIcon } from 'lucide-react';
import baseapi from '../services/baseapi';

interface DropdownItem {
    id?: string | number | null;
    [key: string]: any;
}

interface SingleSelectApiDropdownProps {
    label?: string;
    value?: DropdownItem | null;
    onChange: (item: DropdownItem | null) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    apiEndpoint: string;
    icon?: LucideIcon | null;
    searchPlaceholder?: string;
    searchInputPlaceholder: string;
    error?: string;
    required?: boolean;
    displayProperty: string;
    secondaryDisplayProperty?: string;
    searchFields: string[];
    additionalQueries?: Record<string, any>;
    additionalParams?: Record<string, any>;
    defaultValue?: boolean;
    getDisplayText?: (item: DropdownItem) => string;
    clearDisabled?: boolean;
    idProperty?: string;
    height?: string;
    responseDataKey?: string;
    showSecondaryDisplayProperty?: boolean;
    buttonClassName?: string;
    filterItem?: (item: DropdownItem) => boolean;
    transformData?: (items: DropdownItem[]) => DropdownItem[];
}

const SingleSelectApiDropdown = React.memo<SingleSelectApiDropdownProps>(({
    label,
    value,
    onChange,
    disabled = false,
    placeholder = "Select item",
    className = "",
    apiEndpoint,
    icon: Icon = null,
    searchPlaceholder = 'Search',
    searchInputPlaceholder,
    error,
    required,
    displayProperty,
    secondaryDisplayProperty,
    searchFields,
    additionalQueries = {},
    additionalParams = {},
    defaultValue = false,
    getDisplayText,
    clearDisabled = false,
    idProperty = 'id',
    showSecondaryDisplayProperty = true,
    responseDataKey = 'data',
    height = 'h-11',
    buttonClassName = '',
    filterItem,
    transformData
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [items, setItems] = useState<DropdownItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [hasInitialLoad, setHasInitialLoad] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Memoize additionalQueries to prevent infinite loops
    const memoizedAdditionalQueries = useRef(additionalQueries);
    const memoizedAdditionalParams = useRef(additionalParams);

    useEffect(() => {
        if (JSON.stringify(memoizedAdditionalQueries.current) !== JSON.stringify(additionalQueries)) {
            memoizedAdditionalQueries.current = additionalQueries;
        }
    }, [additionalQueries]);

    useEffect(() => {
        if (JSON.stringify(memoizedAdditionalParams.current) !== JSON.stringify(additionalParams)) {
            memoizedAdditionalParams.current = additionalParams;
        }
    }, [additionalParams]);

    // Calculate dropdown position when opening
    const calculateDropdownPosition = useCallback(() => {
        if (!buttonRef.current) return;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 280; // Adjusted for compact height
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        let position: 'bottom' | 'top' = 'bottom';
        let style: React.CSSProperties = {
            width: `${buttonRect.width}px`,
            left: `${buttonRect.left}px`,
            position: 'fixed'
        };

        if (spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight + 20) {
            position = 'top';
            style.bottom = `${windowHeight - buttonRect.top + 8}px`;
        } else {
            position = 'bottom';
            style.top = `${buttonRect.bottom + 8}px`;
        }

        setDropdownPosition(position);
        setMenuStyle(style);
    }, []);

    const updatePosition = useCallback(() => {
        if (isOpen) {
            calculateDropdownPosition();
        }
    }, [isOpen, calculateDropdownPosition]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    // Debounce search function
    const debounce = useCallback((func: Function, delay: number) => {
        let timeoutId: number;
        return (...args: any[]) => {
            clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => func.apply(null, args), delay);
        };
    }, []);

    // Get nested property value using dot notation
    const getNestedProperty = useCallback((obj: any, path: string) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
    }, []);

    // Get ID from item using configurable idProperty
    const getItemId = useCallback((item: DropdownItem) => {
        return getNestedProperty(item, idProperty);
    }, [getNestedProperty, idProperty]);

    // Auto-select first item if defaultValue is true and no value is selected
    const handleAutoSelect = useCallback((itemsList: DropdownItem[]) => {
        if (defaultValue && !value && itemsList.length > 0) {
            const firstItem = itemsList[0];
            onChange(firstItem);
        }
    }, [defaultValue, value, onChange]);

    // API call to fetch items
    const fetchItems = useCallback(async (search: string = '') => {
        if (showSearch && search.trim() === '') {
            setItems([]);
            return;
        }
        setIsLoading(true);
        try {
            const queryObj: Record<string, any> = { ...memoizedAdditionalQueries.current };
            if (search.trim()) {
                const searchConditions = searchFields.map(field => ({
                    [field]: { $ilike: `%${search}%` }
                }));
                queryObj.$or = searchConditions;
            }
            const params = {
                query: JSON.stringify(queryObj),
                limit: search.trim() ? 10 : 20,
                ...memoizedAdditionalParams.current
            };
            const response = await baseapi.get(apiEndpoint, { params });
            if (response.data.status) {
                let transformedItems = response.data[responseDataKey] || [];
                if (filterItem) {
                    transformedItems = transformedItems.filter(filterItem);
                }
                if (transformData) {
                    transformedItems = transformData(transformedItems);
                }
                setItems(transformedItems);
                if (!showSearch) {
                    handleAutoSelect(transformedItems);
                }
            }
        } catch (error) {
            console.error(`Error fetching data from ${apiEndpoint}:`, error);
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    }, [apiEndpoint, searchFields, showSearch, handleAutoSelect, filterItem, transformData]);

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce((term: string) => {
            fetchItems(term);
        }, 300),
        [fetchItems]
    );

    // Initial API call on component mount when defaultValue is true
    useEffect(() => {
        if (defaultValue && !hasInitialLoad) {
            fetchItems();
            setHasInitialLoad(true);
        }
    }, [defaultValue, hasInitialLoad, fetchItems]);

    // Single fetch only on dropdown open
    const prevIsOpen = useRef(false);
    useEffect(() => {
        if (isOpen && !prevIsOpen.current) {
            if (!defaultValue || hasInitialLoad) {
                fetchItems();
            }
        }
        prevIsOpen.current = isOpen;
    }, [isOpen, defaultValue, hasInitialLoad, fetchItems]);

    // Handle search term changes (on search input)
    useEffect(() => {
        if (showSearch) {
            if (searchTerm.trim()) {
                debouncedSearch(searchTerm);
            } else {
                setItems([]);
            }
        }
    }, [searchTerm, showSearch, debouncedSearch]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(target) &&
                (!menuRef.current || !menuRef.current.contains(target))
            ) {
                setIsOpen(false);
                setShowSearch(false);
                setSearchTerm('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Handle escape key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
                setShowSearch(false);
                setSearchTerm('');
                buttonRef.current?.focus();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    // Focus search input when it becomes visible
    useEffect(() => {
        if (showSearch && isOpen && searchInputRef.current) {
            const timer = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [showSearch, isOpen]);

    const toggleDropdown = () => {
        if (!disabled) {
            calculateDropdownPosition();
            setIsOpen(!isOpen);
            if (isOpen) {
                setShowSearch(false);
                setSearchTerm('');
            }
        }
    };

    const handleItemSelect = (item: DropdownItem) => {
        onChange(item);
        setIsOpen(false);
        setShowSearch(false);
        setSearchTerm('');
    };

    const handleSearchToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowSearch(true);
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 50);
    };

    const handleClearSelection = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    const getDisplayName = useCallback((item: DropdownItem) => {
        if (getDisplayText) return getDisplayText(item);
        const displayValue = getNestedProperty(item, displayProperty);
        return displayValue?.toString() || getItemId(item)?.toString() || '';
    }, [getDisplayText, displayProperty, getNestedProperty, getItemId]);

    const getSecondaryDisplayName = (item: DropdownItem) => {
        if (!secondaryDisplayProperty) return null;
        const secondaryValue = getNestedProperty(item, secondaryDisplayProperty);
        return secondaryValue?.toString() || null;
    };

    return (
        <div className={`space-y-1.5 ${className}`} ref={dropdownRef}>
            {label && (
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-300">
                    {Icon && <Icon className="w-3 h-3 text-orange-400" aria-hidden="true" />}
                    {label}
                    {required && <span className='text-red-400'>*</span>}
                </label>
            )}

            <div className="relative">
                <button
                    ref={buttonRef}
                    onClick={toggleDropdown}
                    disabled={disabled}
                    className={`relative w-full cursor-pointer rounded-lg border border-gray-700 bg-gray-900 py-2 pl-3 pr-9 text-left text-sm shadow-md
                        transition-all duration-300 hover:border-orange-500/50 hover:bg-gray-800/80
                        focus:border-orange-500 focus:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500/20
                        disabled:opacity-50 disabled:cursor-not-allowed ${height} ${buttonClassName}`}
                >
                    <div className="flex items-center gap-2 h-full">
                        <div className="flex-1 min-w-0 h-full flex items-center">
                            {value && getDisplayName(value) ? (
                                <div className="flex flex-col justify-center min-w-0">
                                    <span className="block truncate text-gray-200 font-medium leading-tight">
                                        {getDisplayName(value)}
                                    </span>
                                    {showSecondaryDisplayProperty && secondaryDisplayProperty && getSecondaryDisplayName(value) && (
                                        <span className="block truncate text-[10px] text-gray-400 mt-0.5 leading-tight">
                                            {getSecondaryDisplayName(value)}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <span className="block truncate text-gray-500 leading-tight">{placeholder}</span>
                            )}
                        </div>
                        {value && (
                            <button
                                onClick={handleClearSelection}
                                className="p-1 hover:bg-gray-700 rounded-full transition-colors shrink-0"
                                type="button"
                                disabled={clearDisabled}
                            >
                                <X className={`w-3.5 h-3.5 text-gray-400 hover:text-orange-400 ${clearDisabled ? 'cursor-not-allowed' : "cursor-pointer"}`} />
                            </button>
                        )}
                    </div>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ChevronsUpDown className={`h-4 w-4 text-orange-400 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </span>
                </button>
                {error && <span className='text-red-400 text-xs mt-1'>{error}</span>}

                {isOpen && createPortal(
                    <div
                        ref={menuRef}
                        className={`fixed z-[99999] max-h-72 overflow-hidden rounded-xl bg-gray-800 shadow-2xl border border-gray-700 animate-in fade-in-0 zoom-in-95 duration-100 ${dropdownPosition === 'top' ? 'origin-bottom' : 'origin-top'}`}
                        style={{ ...menuStyle }}
                    >
                        <div className="flex flex-col h-full">
                            {showSearch ? (
                                <div className="p-2 border-b border-gray-700/50 bg-gray-800/80">
                                    <div className="relative">
                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            className="w-full bg-gray-900 border border-gray-600 rounded-lg pl-8 pr-3 py-1.5 text-sm text-white focus:border-orange-500 focus:outline-none transition-all"
                                            placeholder={searchInputPlaceholder}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="p-1.5 border-b border-gray-700/50">
                                    <button
                                        onClick={handleSearchToggle}
                                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-700/50 rounded-lg transition-colors"
                                        type="button"
                                    >
                                        <Search className="w-3.5 h-3.5" />
                                        {searchPlaceholder}
                                    </button>
                                </div>
                            )}

                            <div className="overflow-y-auto custom-scroll overflow-x-hidden flex-1 max-h-[220px]">
                                {isLoading && items.length === 0 ? (
                                    <div className="p-6 text-center">
                                        <div className="w-5 h-5 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin mx-auto mb-2" />
                                        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">Loading...</span>
                                    </div>
                                ) : items.length > 0 ? (
                                    items.map((item) => {
                                        const itemId = getItemId(item);
                                        const isSelected = value && getItemId(value) === itemId;
                                        const displayName = getDisplayName(item);
                                        const secondaryDisplayName = getSecondaryDisplayName(item);
                                        return (
                                            <button
                                                key={itemId}
                                                onClick={() => handleItemSelect(item)}
                                                className={`w-full relative cursor-pointer select-none py-2 px-3 text-sm text-left transition-all duration-150
                                                    ${isSelected ? "bg-orange-500/10 text-white" : "text-gray-300 hover:bg-gray-700/50"}`}
                                                type="button"
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    {Icon && (
                                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-orange-500/20' : 'bg-gray-700'}`}>
                                                            <Icon className={`w-2.5 h-2.5 ${isSelected ? 'text-orange-400' : 'text-gray-400'}`} />
                                                        </div>
                                                    )}
                                                    <div className="min-w-0 flex-1">
                                                        <span className={`block truncate ${isSelected ? "font-semibold" : "font-medium"}`}>
                                                            {displayName}
                                                        </span>
                                                        {secondaryDisplayProperty && secondaryDisplayName && (
                                                            <span className="block truncate text-[10px] text-gray-500 mt-0.5">
                                                                {secondaryDisplayName}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {isSelected && (
                                                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-orange-400">
                                                        <Check className="h-3.5 w-3.5" />
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className="p-6 text-center">
                                        <p className="text-xs text-gray-400 font-medium">
                                            {showSearch && searchTerm.trim() === '' ? 'Type to search...' :
                                                showSearch ? 'No results found' : 'No data available'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
});

export default SingleSelectApiDropdown;
